import { create } from 'zustand';
import { supabase } from './supabase';
import type { Bike, Case, Client, Document, Payment } from './supabase';

interface AppState {
  cases: Case[];
  bikes: Bike[];
  clients: Client[];
  documents: Document[];
  payments: Payment[];
  loading: boolean;
  error: string | null;
  fetchCases: () => Promise<void>;
  fetchBikes: () => Promise<void>;
  fetchClients: () => Promise<void>;
  createCase: (caseData: Partial<Case>) => Promise<Case | null>;
  updateCase: (id: string, updates: Partial<Case>) => Promise<void>;
  createBike: (bikeData: Partial<Bike>) => Promise<Bike | null>;
  updateBikeStatus: (id: string, status: string) => Promise<void>;
  processDocument: (file: File, caseId: string) => Promise<void>;
  recordPayment: (paymentData: Partial<Payment>) => Promise<void>;
}

export const useStore = create<AppState>((set, get) => ({
  cases: [],
  bikes: [],
  clients: [],
  documents: [],
  payments: [],
  loading: false,
  error: null,

  fetchCases: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('*, client:clients(*), bike:bikes(*), documents(*), payments(*)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ cases: data || [] });
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  fetchBikes: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('bikes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ bikes: data || [] });
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  fetchClients: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ clients: data || [] });
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  createCase: async (caseData) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('cases')
        .insert([{ ...caseData, status: 'new_matter' }])
        .select()
        .single();

      if (error) throw error;
      
      const cases = get().cases;
      set({ cases: [data, ...cases] });
      
      return data;
    } catch (error) {
      set({ error: error.message });
      return null;
    } finally {
      set({ loading: false });
    }
  },

  updateCase: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('cases')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      const cases = get().cases.map(c => 
        c.id === id ? { ...c, ...updates } : c
      );
      set({ cases });
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  createBike: async (bikeData) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('bikes')
        .insert([{ ...bikeData, status: 'available' }])
        .select()
        .single();

      if (error) throw error;
      
      const bikes = get().bikes;
      set({ bikes: [data, ...bikes] });
      
      return data;
    } catch (error) {
      set({ error: error.message });
      return null;
    } finally {
      set({ loading: false });
    }
  },

  updateBikeStatus: async (id, status) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('bikes')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      const bikes = get().bikes.map(b => 
        b.id === id ? { ...b, status } : b
      );
      set({ bikes });
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  processDocument: async (file: File, caseId: string) => {
    set({ loading: true, error: null });
    try {
      const { createWorker } = await import('tesseract.js');
      const worker = await createWorker();
      
      const result = await worker.recognize(file);
      await worker.terminate();

      const { error } = await supabase
        .from('documents')
        .insert([{
          case_id: caseId,
          filename: file.name,
          type: 'claim',
          ocr_data: result.data,
        }]);

      if (error) throw error;

      await get().fetchCases();
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  recordPayment: async (paymentData) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('payments')
        .insert([paymentData]);

      if (error) throw error;

      await get().fetchCases();
    } catch (error) {
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },
}));